import React, { useState, useEffect } from "react";
import { CheckCircle, Clock, MapPin, Phone, MessageCircle, AlertCircle, Gift, Star, Instagram } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const BROSUR = "https://customer-assets.emergentagent.com/job_tozan-modern/artifacts/jlnnd91u_Brosur.jpeg";

const PROMO_STYLES = [
  { icon: Star,        color: "#d4af37" },
  { icon: Gift,        color: "#198530" },
  { icon: AlertCircle, color: "#7c3aed" },
];

export default function InformasiPendaftaran() {
  const { waUrl } = useSettings();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/registration-info").then((res) => setInfo(res.data)).catch(() => setInfo(null));
  }, []);

  if (!info) return <div style={{ paddingTop: "120px" }} className="text-center text-gray-400 pb-20">Memuat informasi pendaftaran...</div>;

  const programs        = info.programs        || [];
  const extracurriculars = info.extracurriculars || [];
  const requirements    = info.requirements    || [];
  const costs           = info.costs           || [];
  const promos          = info.promos          || [];

  return (
    <div data-testid="pendaftaran-page" style={{ paddingTop: "60px" }}>

      {/* Header */}
      <div className="py-7 sm:py-10 text-white text-center relative overflow-hidden" style={{ backgroundColor: "#198530" }}>
        <div className="absolute inset-0" style={{ opacity: 0.05 }}>
          <svg width="100%" height="100%"><defs><pattern id="dots3" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="15" cy="15" r="2" fill="white" /></pattern></defs><rect width="100%" height="100%" fill="url(#dots3)" /></svg>
        </div>
        <div className="relative z-10 px-4">
          <p className="text-xs sm:text-sm uppercase tracking-widest mb-1" style={{ color: "#d4af37" }}>Penerimaan Siswa Baru</p>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold">{info.headline}</h1>
          <p className="mt-2 text-xs sm:text-sm" style={{ color: "#bbf7d0" }}>{info.subtitle}</p>
          {info.status_text && (
            <div className="inline-block mt-3 px-4 py-1.5 rounded-full text-sm font-bold" style={{ backgroundColor: "#d4af37", color: "#044712" }}>
              {info.status_text}
            </div>
          )}
        </div>
      </div>

      {/* Brosur + Info */}
      <section className="py-7 sm:py-10" style={{ backgroundColor: "#F8FAF8" }}>
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-7 items-start">

          {/* Brosur */}
          <div>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="pendaftaran-hubungi-btn"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm mb-3 transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: "#25d366", color: "white" }}>
              <MessageCircle size={18} /> Hubungi Kami via WhatsApp
            </a>
            {/* Social Media */}
            <div className="flex gap-3 mb-3">
              <a href="https://instagram.com/ra.al.armi" target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)" }}>
                <Instagram size={16} /> @ra.al.armi
              </a>
              <a href="https://instagram.com/alarmimis" target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)" }}>
                <Instagram size={16} /> @alarmimis
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg" style={{ border: "2px solid #d4af37" }}>
              <img src={BROSUR} alt="Brosur Pendaftaran" className="w-full" loading="lazy" />
            </div>
          </div>

          {/* Info */}
          <div className="space-y-3">
            {/* Kuota */}
            <div className="rounded-xl p-4 text-white text-center" style={{ backgroundColor: "#198530" }}>
              <p className="text-3xl sm:text-4xl font-bold">{info.quota}</p>
              <p className="text-sm font-semibold mt-0.5" style={{ color: "#d4af37" }}>{info.quota_label}</p>
            </div>

            {/* Program Unggulan */}
            <div className="bg-white rounded-xl p-4" style={{ border: "1px solid #dcfce7" }}>
              <h3 className="font-bold mb-2 text-sm" style={{ color: "#198530" }}>Program Unggulan</h3>
              <ul className="space-y-1.5">
                {programs.map((prog, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                    <CheckCircle size={14} style={{ color: "#198530", flexShrink: 0 }} /> {prog}
                  </li>
                ))}
              </ul>
            </div>

            {/* Ekstrakurikuler — hanya mobile */}
            <div className="bg-white rounded-xl p-4 md:hidden" style={{ border: "1px solid #dcfce7" }}>
              <h3 className="font-bold mb-2 text-sm" style={{ color: "#198530" }}>Kegiatan Ekstrakurikuler</h3>
              <div className="flex flex-wrap gap-1.5">
                {extracurriculars.map((e, idx) => (
                  <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>{e}</span>
                ))}
              </div>
            </div>

            {/* Syarat */}
            <div className="bg-white rounded-xl p-4" style={{ border: "1px solid #dcfce7" }}>
              <h3 className="font-bold mb-2 text-sm" style={{ color: "#198530" }}>Syarat Pendaftaran</h3>
              <ul className="space-y-1.5">
                {requirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                    <CheckCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: "#198530" }} /> {req}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Biaya */}
      <section className="py-7 sm:py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-4 sm:mb-6">
            <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "#d4af37" }}>Rincian Pembiayaan</p>
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Biaya Pendidikan</h2>
          </div>
          <div className="rounded-xl overflow-hidden shadow-md" style={{ border: "1px solid #e5e7eb" }}>
            <table className="w-full" data-testid="biaya-table">
              <thead>
                <tr style={{ backgroundColor: "#198530", color: "white" }}>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold">Keterangan</th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs sm:text-sm font-semibold">Biaya Normal</th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs sm:text-sm font-semibold">Yang Dibayar</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((cost, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#F8FAF8" }}>
                    <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-gray-800">
                      {cost.item}
                      {cost.note && <span className="block text-xs mt-0.5" style={{ color: "#198530" }}>{cost.note}</span>}
                    </td>
                    <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-right text-gray-500">
                      <span className={cost.bayar !== cost.normal ? "line-through" : ""}>{cost.normal}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 text-xs sm:text-sm text-right font-semibold"
                      style={{ color: cost.bayar === "Gratis" ? "#198530" : "#1a202c" }}>{cost.bayar}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: "#198530", color: "white" }}>
                  <td className="px-3 sm:px-6 py-3 font-bold text-xs sm:text-sm" colSpan={2}>Total yang Dibayar</td>
                  <td className="px-3 sm:px-6 py-3 font-bold text-right text-xs sm:text-sm" style={{ color: "#d4af37" }}>{info.total_text}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Promo Spesial */}
      {promos.length > 0 && (
        <section className="py-7 sm:py-10" style={{ backgroundColor: "#198530" }}>
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-4 sm:mb-6">
              <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "#d4af37" }}>
                Penawaran Istimewa
              </p>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Program Kami</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {promos.map((promo, idx) => {
                const style = PROMO_STYLES[idx % PROMO_STYLES.length];
                const Icon  = style.icon;
                return (
                  <div key={idx} data-testid={`promo-${idx}`}
                    className="bg-white rounded-xl p-4 flex items-start gap-3 transition-all duration-300"
                    style={{ border: `1px solid ${style.color}30` }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = style.color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = `${style.color}30`; }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${style.color}15` }}>
                      <Icon size={22} style={{ color: style.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {promo.badge && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-white text-xs font-bold mb-1" style={{ backgroundColor: style.color }}>
                          {promo.badge}
                        </span>
                      )}
                      <h3 className="font-bold text-gray-900 text-xs sm:text-sm mb-0.5">{promo.title}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed">{promo.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Waktu & Tempat + CTA */}
      <section className="py-7 sm:py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: "#eef9f0", border: "1px solid #dcfce7" }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#198530" }}>
                <Clock size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm mb-0.5">Waktu Pendaftaran</h3>
                <p className="text-gray-600 text-xs">{info.waktu}</p>
              </div>
            </div>
            <div className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: "#fefce8", border: "1px solid #fef08a" }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#d4af37" }}>
                <MapPin size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm mb-0.5">Tempat Pendaftaran</h3>
                <p className="text-gray-600 text-xs">{info.tempat}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl p-5 text-white text-center" style={{ backgroundColor: "#044712" }}>
            <h3 className="text-lg sm:text-2xl font-bold mb-2">Siap Mendaftarkan Putra/Putri Anda?</h3>
            <p className="mb-4 text-xs sm:text-sm" style={{ color: "#bbf7d0" }}>
              Hubungi kami sekarang untuk informasi lebih lanjut dan proses pendaftaran.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pendaftaran-wa-button"
                className="inline-flex items-center justify-center gap-2 text-white px-5 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90"
                style={{ backgroundColor: "#25d366" }}
              >
                <MessageCircle size={18} />
                0812-1421-6628
              </a>
              <a href="tel:08121245-5254" data-testid="pendaftaran-phone-button"
                className="inline-flex items-center justify-center gap-2 text-white px-5 py-2.5 rounded-xl font-bold text-sm"
                style={{ backgroundColor: "#198530" }}>
                <Phone size={17} /> 0812-1245-5254
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
